/**
 * What the Übersicht's Finanzen card is allowed to say about this tenant's
 * invoices (TINV-2).
 *
 * ONE SMALL READ, AGAINST THE WORLD THE ORGANIZATION ACTUALLY BILLS IN. Until
 * TINV-2 the card read the native `invoices` table unconditionally — which is
 * silent for every organization whose `invoice_creation_provider` is `bexio`,
 * because the native creation path refuses to write there at all. A card that
 * says "keine Rechnungen erfasst" about a tenant with twelve Bexio invoices is
 * not merely unhelpful; it is the most damaging sentence this surface can print.
 *
 * SO THE READ IS GATED ON THE CONFIRMED PROVIDER AND NOT ON A GUESS. While the
 * capability answer is in flight, and after it failed, `source` is `null` and
 * NOTHING is fetched. Failing over to native "just to show something" would
 * reintroduce exactly the untruth above, one layer further out.
 *
 * TWO DIFFERENT LIMITS, FOR TWO DIFFERENT QUESTIONS — this is the part worth
 * reading twice:
 *
 *   bexio   `limit: 1`. The card states a COUNT and no payment claim, and the
 *           count is the endpoint's `total`, which is computed over the whole
 *           deduplicated set rather than the page. One row is all that has to
 *           travel. Asking for 200 invoices to learn a number the envelope
 *           already carries would be a full history download per tenant.
 *
 *   native  `limit: 20`, unchanged. This is NOT an oversight against the brief's
 *           "small limit if possible". The native card states overdue and unpaid
 *           COUNTS, and `selectInvoiceOverview` only permits them when the loaded
 *           window provably holds every invoice (`total <= loaded`). Dropping to
 *           `limit: 1` would make that gate false for every tenant with two or
 *           more invoices, silently demoting a card that states the payment
 *           state today into one that states nothing. The brief forbids
 *           worsening the existing conservative gates, and this is the shape
 *           that keeps them.
 *
 * THIS IS NOT THE TAB'S READ. The tab fetches a page of ten, only once it is
 * opened, from its own state. The two are deliberately separate: one answers
 * "is there anything to worry about" for a card that is always visible, the
 * other answers "show me the documents" for a panel that usually is not. They
 * are never identical requests, so neither can be deduplicated into the other.
 *
 * NO PAYMENT ARITHMETIC LIVES HERE. The native counts come from
 * `selectInvoiceOverview`, whose input `status` is the backend's own
 * `effective_status` resolved against the Swiss business day. The Bexio wire
 * carries no such canonical field — only the provider's `bexio_status` — so this
 * hook returns a count for that world and no derived payment state at all.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchAdminInvoices } from "../../../../api/adminData";
import { fetchBexioTenantInvoices } from "../../../../api/adminData/bexio";
import { selectInvoiceOverview } from "../overview/overviewSelectors";
import {
  currentTenantInvoiceSource,
  tenantInvoiceSourceLabels,
} from "./tenantInvoiceSource";

/** See the docstring: the window the native completeness gate needs. */
export const NATIVE_OVERVIEW_LIMIT = 20;

/** See the docstring: only `total` is read, so one row is enough. */
export const BEXIO_OVERVIEW_LIMIT = 1;

const IDLE = { loading: false, error: null, data: null };

export default function useTenantInvoiceOverview({
  tenantId,
  provider,
  capabilitiesLoading = false,
  capabilitiesError = null,
}) {
  const source = currentTenantInvoiceSource({
    loading: capabilitiesLoading,
    error: capabilitiesError,
    provider,
  });
  const sourceLabel = tenantInvoiceSourceLabels({ source, provider: source })
    .active;

  const [state, setState] = useState(IDLE);

  // The (tenant, source) pair whose read is loaded or in flight. Guards both a
  // repeat fetch on every re-render and a response for a previous tenant — or a
  // previous world — landing after a newer one.
  const loadedFor = useRef(null);

  const load = useCallback((key, id, src) => {
    setState({ loading: true, error: null, data: null });
    const request =
      src === "bexio"
        ? fetchBexioTenantInvoices({
            tenantId: id,
            limit: BEXIO_OVERVIEW_LIMIT,
            offset: 0,
          })
        : fetchAdminInvoices({ tenantId: id, limit: NATIVE_OVERVIEW_LIMIT });

    request
      .then((d) => {
        if (loadedFor.current !== key) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        const total = Number.isFinite(Number(d?.total))
          ? Number(d.total)
          : null;
        setState({
          loading: false,
          error: null,
          // The Bexio arm is a COUNT ONLY. No `complete`, no overdue and no
          // unpaid: that wire carries no canonical payment status, so there is
          // nothing honest to derive from it.
          data:
            src === "bexio"
              ? { total }
              : selectInvoiceOverview(items, total),
        });
      })
      .catch(() => {
        if (loadedFor.current !== key) return;
        setState({
          loading: false,
          error: "Rechnungen konnten nicht geladen werden.",
          data: null,
        });
      });
  }, []);

  useEffect(() => {
    // No tenant, or no confirmed world yet: hold, and read nothing.
    if (!tenantId || !source) {
      loadedFor.current = null;
      setState(IDLE);
      return;
    }
    const key = `${tenantId}::${source}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    load(key, tenantId, source);
  }, [tenantId, source, load]);

  return {
    source,
    sourceLabel,
    loading: capabilitiesLoading || state.loading,
    // The capability failure is surfaced as the card's error too: without the
    // provider there is no world to read, so "we could not load the invoices"
    // is the honest thing to say rather than an empty card.
    error: capabilitiesError || state.error,
    ...(state.data || {}),
  };
}
